/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import type { InputBaseComponentProps, TextFieldProps } from '@mui/material';
import { TextField } from '@mui/material';
import React from 'react';
import { useController, useFormContext } from 'react-hook-form';
import type { NumberInputMode } from '@opengeoweb/shared';
import { convertNumericInputValue, getAllowedKeys } from '@opengeoweb/shared';
import { getErrors } from './formUtils';
import ReactHookFormFormControl from './ReactHookFormFormControl';
import type { ReactHookFormInput } from './types';
import { defaultProps } from './utils';

type ReactHookFormNumberFieldProps = Partial<TextFieldProps> &
  ReactHookFormInput<{
    inputMode?: NumberInputMode;
    // Runs before React Hook Form updates the field value, so callers can
    // prepare state that would otherwise be mutated during the update.
    onBeforeChange?: () => void;
  }>;

const ReactHookFormNumberField: React.FC<ReactHookFormNumberFieldProps> = ({
  name,
  label,
  defaultValue = null,
  rules,
  disabled = false,
  inputMode = 'numeric',
  helperText = '',
  className,
  size = 'medium',
  sx,
  onBeforeChange = (): void => {},
  onChange = (): void => {},
  isReadOnly,
  ...otherProps
}: ReactHookFormNumberFieldProps) => {
  const { control } = useFormContext();
  const {
    field: { onChange: onChangeField, value, ref },
    formState: { errors: formErrors },
  } = useController({
    name,
    control,
    rules,
    defaultValue,
    ...defaultProps,
  });

  const errors = getErrors(name!, formErrors);

  const allowedKeyes = getAllowedKeys(inputMode);

  const onPaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    const clipboardText = event.clipboardData.getData('text/plain');

    const inputElement = event.target as HTMLInputElement;

    const before = inputElement.value.slice(0, inputElement.selectionStart!);
    const after = inputElement.value.slice(inputElement.selectionEnd!);

    const newValue = before + clipboardText + after;

    if (inputMode === 'numeric' && !/^-?[0-9]+$/.exec(newValue)) {
      event.preventDefault();
    }

    if (inputMode === 'decimal' && !/^-?[0-9]*(.[0-9]+)?$/.exec(newValue)) {
      event.preventDefault();
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    const isMac = window.navigator.userAgent.includes(' Mac ');

    if ((isMac && event.metaKey) || (!isMac && event.ctrlKey)) {
      if (['c', 'v', 'x'].includes(event.key.toLowerCase())) {
        return;
      }
    }

    if (
      !allowedKeyes.includes(event.key) ||
      // only allow one seperator
      (event.key === '.' &&
        (event.target as HTMLInputElement).value.includes('.')) ||
      // only allow one -
      (event.key === '-' &&
        (event.target as HTMLInputElement).value.includes('-'))
    ) {
      event.preventDefault();
    }
  };

  // Transform number value to string for display, following React Hook Form transform pattern
  const formatValueForDisplay = React.useCallback(
    (numValue: number | null | undefined): string => {
      if (numValue === null || numValue === undefined || isNaN(numValue)) {
        return '';
      }
      return String(numValue);
    },
    [],
  );

  // Local state to preserve string precision during editing (e.g., trailing zeros), only sync from form value when not actively editing
  const [localValue, setLocalValue] = React.useState<string | null>(null);
  const isFocusedRef = React.useRef(false);
  const previousValueRef = React.useRef<number | null | undefined>(value);
  const lastSetValueRef = React.useRef<number | null | undefined>(null);

  // Sync local value from form value when value changes externally, but only when not actively editing and the change wasn't from our own update
  React.useEffect(() => {
    if (!isFocusedRef.current && value !== previousValueRef.current) {
      const wasOurUpdate =
        lastSetValueRef.current !== null && lastSetValueRef.current === value;
      if (!wasOurUpdate) {
        setLocalValue(null); // Clear local value to use form value
      } else if (localValue !== null) {
        // It was our update, but check if localValue still represents this number accurately
        const numFromLocal = convertNumericInputValue(localValue, inputMode);
        if (numFromLocal !== value) {
          // Local value no longer matches form value, clear it
          setLocalValue(null);
        }
      }
      previousValueRef.current = value;
    }
  }, [value, localValue, inputMode]);

  // Compute display value: prefer local value if it has more precision than number representation
  const displayValue = React.useMemo(() => {
    // Always use local value if it exists and represents the same number as form value
    if (localValue !== null) {
      const numFromLocal = convertNumericInputValue(localValue, inputMode);
      const valuesMatch =
        numFromLocal === value ||
        (numFromLocal === null && value === null) ||
        (numFromLocal !== null &&
          typeof numFromLocal === 'number' &&
          typeof value === 'number' &&
          isNaN(numFromLocal) &&
          isNaN(value));
      if (valuesMatch) {
        return localValue;
      }
    }
    // When no local value or it doesn't match, use formatted form value
    return formatValueForDisplay(value);
  }, [value, localValue, inputMode, formatValueForDisplay]);

  return (
    <ReactHookFormFormControl
      className={className}
      disabled={disabled}
      errors={errors}
      isReadOnly={isReadOnly}
      sx={sx}
    >
      <TextField
        disabled={disabled}
        error={!!errors}
        helperText={helperText}
        inputMode={inputMode}
        inputRef={ref}
        label={label}
        name={name}
        onBlur={(evt: React.FocusEvent<HTMLInputElement>): void => {
          isFocusedRef.current = false;
          // Keep local value on blur to preserve precision (e.g., trailing zeros), it will be cleared only if form value changes externally
          previousValueRef.current = value;
          otherProps.onBlur?.(evt);
        }}
        onChange={(evt: React.ChangeEvent<HTMLInputElement>): void => {
          const { value: inputValue } = evt.target;
          // Update local value to preserve string precision during editing
          setLocalValue(inputValue);
          // Convert to number for form value
          const numericValue = convertNumericInputValue(inputValue, inputMode);
          lastSetValueRef.current = numericValue;
          onBeforeChange();
          onChangeField(numericValue);
          onChange(null!);
        }}
        onFocus={(evt: React.FocusEvent<HTMLInputElement>): void => {
          isFocusedRef.current = true;
          // Initialize local value with current display value when focusing
          if (localValue === null) {
            setLocalValue(formatValueForDisplay(value));
          }
          otherProps.onFocus?.(evt);
        }}
        onKeyDown={onKeyDown}
        size={size}
        type="text"
        value={displayValue}
        variant="filled"
        {...otherProps}
        slotProps={{
          ...otherProps.slotProps,
          input: {
            ...(isReadOnly && {
              readOnly: true,
            }),
            onPaste,
          },

          htmlInput: {
            ...(otherProps.slotProps?.htmlInput as InputBaseComponentProps),
            inputMode,
          },
        }}
      />
    </ReactHookFormFormControl>
  );
};
export default ReactHookFormNumberField;
